import {
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  ArrowUpDown,
} from "lucide-react";
import Pagination from "../PaginationComp";

// Status configuration
const statusConfig = {
  active: {
    color: "bg-emerald-100 text-emerald-800",
    icon: CheckCircle,
    label: "Active",
  },
  trial: {
    color: "bg-blue-100 text-blue-800",
    icon: Clock,
    label: "Trial",
  },
  expired: {
    color: "bg-amber-100 text-amber-800",
    icon: AlertCircle,
    label: "Expired",
  },
  cancelled: {
    color: "bg-red-100 text-red-800",
    icon: XCircle,
    label: "Cancelled",
  },
};


const PlanHistoryTab = ({
  history = [],
  pagination,
  sortOrder,
  onSortChange,
  onPageChange,
  loading = false,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading Skeleton for Header */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-pulse">
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="h-6 bg-gray-300 rounded w-48 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-64"></div>
          </div>

          {/* Loading Skeleton for History Items */}
          <div className="divide-y divide-gray-200">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-gray-200 w-9 h-9"></div>
                      <div className="flex-1 space-y-3">
                        <div className="h-5 bg-gray-200 rounded w-32"></div>
                        <div className="h-4 bg-gray-200 rounded w-48"></div>
                        <div className="h-4 bg-gray-200 rounded w-40"></div>
                      </div>
                    </div>
                  </div>
                  <div className="sm:text-right">
                    <div className="h-8 bg-gray-200 rounded w-20 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Loading Skeleton for Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="h-4 bg-gray-200 rounded w-40"></div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!history || history.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 text-center">
        <div className="w-16 h-16 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          No Plan History
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          Your subscription history will appear here once you start using our
          services.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter and Sort */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">

          <div>
            <button
              onClick={() =>
                onSortChange(sortOrder === "desc" ? "asc" : "desc")
              }
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
              disabled={loading}
            >
              {sortOrder === "desc" ? "Newest First" : "Oldest First"}
              <ArrowUpDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {history.map((item, index) => {
            const status = item.derived_status?.toLowerCase();
            const config = statusConfig[status] || statusConfig.expired;
            const Icon = config.icon;

            return (
              <div
                key={item._id || index}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Status and Plan Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-lg ${config.color.replace("text-", "bg-").split(" ")[0]}`}
                      >
                        <Icon
                          className={`w-5 h-5 ${config.color.split(" ")[1]}`}
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {item.title}
                          </h4>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}
                          >
                            {config.label}
                          </span>
                        </div>

                        <p className="text-gray-600 mb-3">
                          {item.description || "Subscription plan"}
                        </p>

                        {/* Plan Period */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">
                            {item.current_period_start
                              ? new Date(
                                  item.current_period_start,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "N/A"}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="font-medium">
                            {item.current_period_end
                              ? new Date(
                                  item.current_period_end,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Amount and Date */}
                  <div className="sm:text-right">
                    {item.amount && (
                      <div className="mb-2">
                        <p className="text-2xl font-bold text-gray-800">
                          ${item.amount}
                        </p>
                        {item.duration_days && (
                          <p className="text-sm text-gray-500">
                            {item.duration_days} day plan
                          </p>
                        )}
                      </div>
                    )}

                    <div className="text-sm text-gray-500">
                      <p className="font-medium">
                        {item.createdAt
                          ? `Activated: ${new Date(
                              item.createdAt,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}`
                          : "Date not available"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                {(item.highlighting_feature) && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {/* Features Array */}
                    {Array.isArray(item.highlighting_feature) &&
                      item.highlighting_feature.length > 0 && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Features ({item.highlighting_feature.length}):
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {item.highlighting_feature.map((feature, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-teal-50 to-blue-50 text-teal-700 rounded-full text-sm border border-teal-100"
                              >
                                <CheckCircle className="w-3 h-3" />
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Single String Feature (fallback) */}
                    {!Array.isArray(item.highlighting_feature) &&
                      item.highlighting_feature && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            Feature:
                          </p>
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-teal-50 to-blue-50 text-teal-700 rounded-full text-sm border border-teal-100">
                            <CheckCircle className="w-3 h-3" />
                            {item.highlighting_feature}
                          </span>
                        </div>
                      )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          {pagination?.totalPages > 1 && (
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.page}
              onPageChange={onPageChange}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanHistoryTab;
